import { useState, useEffect } from 'react'
import {
  Box, Container, Typography, Paper, Grid, Button,
  Chip, Alert, CircularProgress, Divider
} from '@mui/material'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import CreditsDisplay from '../components/credits/CreditsDisplay'
import { createCheckoutSession, getStripePackages, StripePackage } from '../api/stripe'

const CreditsPage = () => {
  const [packages, setPackages] = useState<StripePackage[]>([])
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creditsRefresh, setCreditsRefresh] = useState(0)
  const { lang } = useLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const successParam = searchParams.get('success')
  const cancelParam = searchParams.get('cancel')

  useEffect(() => {
    getStripePackages()
      .then(setPackages)
      .catch(() => setError('Could not load packages'))
      .finally(() => setPageLoading(false))
  }, [])

  // Refresh credits after returning from Stripe success
  useEffect(() => {
    if (successParam === '1') {
      setCreditsRefresh(n => n + 1)
    }
  }, [successParam])

  const handleBuy = async (packageId: string) => {
    setError(null)
    setLoading(true)
    try {
      const result = await createCheckoutSession(packageId)
      if (result.session_url) {
        window.location.href = result.session_url
      } else {
        setError('No checkout URL received')
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Checkout failed')
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (pkg: StripePackage) =>
    lang === 'pl' ? `${pkg.price_pln} PLN` : `€${pkg.price_eur}`

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {lang === 'pl' ? 'Kup kredyty' : 'Buy Credits'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {lang === 'pl'
              ? 'Kredyty są używane do generowania zdjęć AI.'
              : 'Credits are used to generate AI images.'}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {lang === 'pl' ? 'Aktualny stan' : 'Current balance'}
          </Typography>
          <CreditsDisplay refreshTrigger={creditsRefresh} variant="compact" />
        </Box>
      </Box>

      {/* Success / cancel banners */}
      {successParam === '1' && (
        <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 3 }}>
          {lang === 'pl'
            ? 'Płatność zakończona! Kredyty zostały dodane do Twojego konta.'
            : 'Payment successful! Credits have been added to your account.'}
        </Alert>
      )}
      {cancelParam === '1' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {lang === 'pl' ? 'Płatność anulowana.' : 'Payment cancelled.'}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Packages */}
      {pageLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {packages.map((pkg) => (
            <Grid item xs={12} sm={6} md={4} key={pkg.id}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: pkg.id === 'standard' ? '2px solid' : '1px solid',
                  borderColor: pkg.id === 'standard' ? 'primary.main' : 'divider',
                  position: 'relative',
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.2s',
                }}
              >
                {pkg.id === 'standard' && (
                  <Chip
                    label={lang === 'pl' ? 'Popularne' : 'Popular'}
                    color="primary"
                    size="small"
                    sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}
                  />
                )}

                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {pkg.name}
                </Typography>

                <Typography variant="h3" fontWeight={800} color="primary" sx={{ my: 1 }}>
                  {pkg.credits}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {lang === 'pl' ? 'kredytów' : 'credits'}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Typography variant="h5" fontWeight={700} sx={{ mt: 1, mb: 3 }}>
                  {formatPrice(pkg)}
                </Typography>

                <Button
                  variant={pkg.id === 'standard' ? 'contained' : 'outlined'}
                  fullWidth
                  size="large"
                  disabled={loading}
                  startIcon={<CreditCardIcon />}
                  onClick={() => handleBuy(pkg.id)}
                  sx={{ mt: 'auto', borderRadius: 2 }}
                >
                  {loading
                    ? <CircularProgress size={20} />
                    : (lang === 'pl' ? 'Kup teraz' : 'Buy now')}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {lang === 'pl'
            ? 'Płatności obsługiwane przez Stripe. Bezpieczne szyfrowanie SSL.'
            : 'Payments powered by Stripe. Secure SSL encryption.'}
        </Typography>
      </Box>
    </Container>
  )
}

export default CreditsPage
